// src/pages/SubscriptionPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import {
  Crown,
  Zap,
  Shield,
  TrendingUp,
  Link2,
  ArrowLeft,
  Loader2,
  AlertCircle,
} from 'lucide-react';

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 1. Check if already Premium
  const isPremium = currentUser?.plan_type === 'premium' && 
                    new Date(currentUser?.premium_expires_at) > new Date();

  useEffect(() => {
    if (isPremium) {
      navigate('/dashboard');
    }
  }, [isPremium, navigate]);

  // 2. Korapay Payment Handler
  const handlePayment = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/payments/initialize', {
        gateway: 'korapay',
        amount: 5000,
        callback_url: window.location.origin + '/dashboard/subscription/verify'
      });

      const { checkout_url, authorization_url, link } = response.data;
      const paymentLink = checkout_url || authorization_url || link;
      
      if (paymentLink) {
        window.location.href = paymentLink;
      } else {
        throw new Error('No payment link received from server.');
      }

    } catch (err) {
      console.error("Payment init failed:", err);
      setError(err.response?.data?.detail || "Failed to initialize payment. Please try again.");
      setLoading(false);
    }
  };

  const features = [
    { icon: Link2, text: 'Connect MT5 Account (Real/Demo)' },
    { icon: TrendingUp, text: 'Unlock Pattern Analytics' },
    { icon: Shield, text: 'Premium Support Access' },
    { icon: Zap, text: 'Real-time Trade Notifications' },
  ];

  return (
    // Background: Light slate / Dark gray
    <div className="h-full overflow-y-auto bg-slate-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4 pb-24 transition-colors duration-300">
      
      <div className="w-full max-w-md">
        
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back
        </button>

        {/* Card Container: White in light, Translucent gray in dark */}
        <div className="bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden relative shadow-sm dark:shadow-none">
          
          {/* Top Gradient - Gold Style (Kept consistent for branding) */}
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 p-6 text-center relative overflow-hidden">
             <div className="absolute inset-0 bg-black/10 pattern-grid-lg"></div>
             <div className="relative z-10">
                <Crown size={40} className="mx-auto text-white mb-2 drop-shadow-lg" />
                <h1 className="text-2xl font-bold text-white drop-shadow-md">Go Premium</h1>
                <p className="text-yellow-100 text-sm mt-1">Unlock your trading potential</p>
             </div>
          </div>

          {/* Content */}
          <div className="p-6">
            
            {/* Price Tag */}
            <div className="text-center mb-6">
              <div className="flex items-baseline justify-center">
                <span className="text-gray-400 text-xl mr-1">₦</span>
                {/* Text: Dark in light mode */}
                <span className="text-5xl font-extrabold text-gray-900 dark:text-white">5,000</span>
                <span className="text-gray-400 text-xl ml-1">/ month</span>
              </div>
            </div>

            {/* Features List */}
            <div className="space-y-3 mb-6">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  {/* Icon Container: Lighter yellow in light mode */}
                  <div className="bg-yellow-100 dark:bg-yellow-600/20 rounded-full p-1">
                    <feature.icon size={16} className="text-yellow-600 dark:text-yellow-400" />
                  </div>
                  {/* Text: Darker in light mode */}
                  <span className="text-gray-600 dark:text-gray-300 text-sm">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl mb-4 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={handlePayment}
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold py-4 px-6 rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 shadow-md"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Processing...
                </>
              ) : (
                <>
                  <Zap size={20} />
                  Pay with Korapay
                </>
              )}
            </button>

            {/* Security Note */}
            <p className="text-center text-gray-400 dark:text-gray-600 text-xs mt-4 flex items-center justify-center gap-1">
              <Shield size={12} />
              Secured by Korapay Encryption
            </p>

          </div>
        </div>
      </div>
    </div>
  );
}